import { useContext, useEffect, useMemo, useState } from "react";
import { SearchPageContext } from "@/search/Langston/SearchPageProvider";
import { LangstonParameterSchema } from "../../LangstonParameterSchema";

interface UseStandardsQueryOptions {
  searchParams?: URLSearchParams;
  getSearchParamValues?: (key: string) => string[];
  ignoreSearchPageContext?: boolean;
}

export const useStandardsQuery = (options?: UseStandardsQueryOptions) => {
  const [queryHasStandardsParam, setQueryHasStandardsParam] = useState(false);

  const searchPageCtx = useContext(SearchPageContext);

  const useSearchPage = !options?.ignoreSearchPageContext && !!searchPageCtx;

  const searchParams = useSearchPage
    ? searchPageCtx!.getters.searchParams
    : options?.searchParams || new URLSearchParams();

  const getSearchParamValues = useSearchPage
    ? searchPageCtx!.handlers.getSearchParamValues
    : options?.getSearchParamValues || (() => []);

  const searchParamsObj = useMemo(
    () => (searchParams ? Object.fromEntries(searchParams.entries()) : {}),
    [searchParams],
  );

  const standardsInQuery = useMemo(
    () => getSearchParamValues(LangstonParameterSchema.standards.key) || [],
    [getSearchParamValues, JSON.stringify(searchParamsObj)],
  );

  const fetchParamsForQuery = useMemo(() => {
    if (standardsInQuery.length > 0) {
      return { metadata_ids: standardsInQuery, ...searchParamsObj };
    }
    if (queryHasStandardsParam) {
      return searchParamsObj;
    }
    return undefined;
  }, [standardsInQuery, queryHasStandardsParam, searchParamsObj]);

  useEffect(() => {
    // Checks necessity to toggle the queryHasStandardsParam
    setQueryHasStandardsParam(standardsInQuery.length > 0);
  }, [standardsInQuery.length]);

  return {
    fetchParamsForQuery,
    searchParamsObj,
    standardsInQuery,
  };
};
